import { useEffect } from "react";

// 8 lingue pubbliche: le 3 nuove (ru, pt = pt-BR, ko = ko-KR) leggono un dizionario EN→lingua caricato solo quando servono.
export const LANGS8 = ["it", "en", "fr", "de", "es", "ru", "pt", "ko"];
export const NEW_LANGS = ["ru", "pt", "ko"];
export const LANG_LABEL = { it: "Italiano", en: "English", fr: "Français", de: "Deutsch", es: "Español", ru: "Русский", pt: "Português (Brasil)", ko: "한국어" };
export const HTML_LANG = { pt: "pt-BR", ko: "ko-KR" };
export const LANG_RE = "it|en|fr|de|es|ru|pt|ko";
export const htmlLang = (l) => HTML_LANG[l] || l;

const DICT = {};
const memo = new WeakMap();
const own = Object.prototype.hasOwnProperty;
const MISS = typeof window !== "undefined" ? (window.__avMiss = new Set()) : new Set();
const tr = (l, v) => {
  if (typeof v === "string") { const d = DICT[l]?.[v]; if (d === undefined && v.trim()) MISS.add(v); return d ?? v; }
  if (!v || typeof v !== "object" || typeof v === "function") return v;
  let m = memo.get(v);
  if (!m) { m = {}; memo.set(v, m); }
  if (!own.call(m, l)) {
    const out = Array.isArray(v) ? v.map((x) => tr(l, x)) : Object.fromEntries(Object.entries(v).map(([k, x]) => [k, tr(l, x)]));
    m[l] = out;
  }
  return m[l];
};

// Ogni oggetto { it, en, … } risponde anche a .ru/.pt/.ko: traduzione dal dizionario, altrimenti testo inglese.
for (const l of NEW_LANGS) {
  if (!own.call(Object.prototype, l)) {
    Object.defineProperty(Object.prototype, l, {
      configurable: true,
      enumerable: false,
      get() { return own.call(this, "en") && own.call(this, "it") ? tr(l, this.en || this.it) : undefined; },
      set(v) { Object.defineProperty(this, l, { value: v, writable: true, enumerable: true, configurable: true }); },
    });
  }
}

export const tl = (l, s) => (l !== "it" && l !== "en" ? DICT[l]?.[s] ?? s : s);
const DICT_LANGS = ["fr", "de", "es", ...NEW_LANGS];
export const missing = [];
export const withNew = (langs) => (langs.includes("en") ? [...langs, ...NEW_LANGS.filter((l) => !langs.includes(l))] : langs);

export async function loadDict(l) {
  if (!DICT_LANGS.includes(l) || DICT[l]) return;
  try {
    const r = await fetch(`/i18n/${l}.json?v=${process.env.REACT_APP_I18N_V || "1"}`);
    DICT[l] = r.ok ? await r.json() : {};
  } catch (e) { DICT[l] = {}; }
  if (l === "ko" || l === "ru") {
    const fam = l === "ko" ? "Noto+Sans+KR:wght@400;600;700&family=Noto+Serif+KR:wght@500;600" : "Cormorant+Garamond:wght@500;600&family=Manrope:wght@400;600;700";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fam}&subset=${l === "ru" ? "cyrillic" : "korean"}&display=swap`;
    document.head.appendChild(link);
    document.documentElement.classList.add(`lang-${l}`);
  }
}

const PDICT = {};
// Nomi e descrizioni prodotto (originali in italiano) tradotti da /i18n/products.{lang}.json, solo in lettura.
export async function localizeProducts(data) {
  const l = pickLang("en");
  if (l === "it" || !data) return data;
  if (!PDICT[l]) {
    try { const r = await fetch(`/i18n/products.${l}.json?v=${process.env.REACT_APP_I18N_V || "1"}`); PDICT[l] = r.ok ? await r.json() : {}; } catch (e) { PDICT[l] = {}; }
  }
  const d = PDICT[l];
  const one = (p) => (p && typeof p === "object" ? { ...p, name: d[p.name] || p.name, description: p.description ? d[p.description] || p.description : p.description } : p);
  return Array.isArray(data) ? data.map(one) : data.items ? { ...data, items: data.items.map(one) } : one(data);
}

// Pagine scritte solo in italiano (AMALFI CLASSIC, Lino 2027): sostituisce i testi visibili con /i18n/pages.{lang}.json.
export function useDomTranslate(ref) {
  useEffect(() => {
    const l = pickLang("en");
    if (l === "it" || !ref.current) return undefined;
    let obs = null;
    let alive = true;
    fetch(`/i18n/pages.${l}.json?v=${process.env.REACT_APP_I18N_V || "1"}`).then((r) => (r.ok ? r.json() : {})).then((d) => {
      if (!alive || !ref.current) return;
      const up = Object.fromEntries(Object.entries(d).map(([k, v]) => [k.toUpperCase(), v.toUpperCase()]));
      const run = () => {
        const w = document.createTreeWalker(ref.current, NodeFilter.SHOW_TEXT);
        for (let n = w.nextNode(); n; n = w.nextNode()) { const t = n.nodeValue.replace(/\s+/g, " ").trim(); const v = d[t] || up[t]; if (v && v !== t) n.nodeValue = v; }
        ref.current.querySelectorAll("[alt],[aria-label]").forEach((e) => ["alt", "aria-label"].forEach((a) => { const v = e.getAttribute(a); if (v && d[v]) e.setAttribute(a, d[v]); }));
      };
      run();
      obs = new MutationObserver(run);
      obs.observe(ref.current, { subtree: true, childList: true, characterData: true });
    }).catch(() => {});
    return () => { alive = false; obs?.disconnect(); };
  }, [ref]);
}

export function pickLang(fallback = "en") {
  let q = null, saved = null;
  try { q = new URLSearchParams(window.location.search).get("lang"); saved = localStorage.getItem("av_lang"); } catch (e) { /* no storage */ }
  const path = window.location.pathname.match(new RegExp(`^/(${LANG_RE})(/|$)`));
  const nav = (navigator.language || "").slice(0, 2);
  return [q, path?.[1], saved, nav].find((l) => LANGS8.includes(l)) || fallback;
}

export function saveLang(l) {
  try { localStorage.setItem("av_lang", l); } catch (e) { /* no storage */ }
  document.documentElement.lang = htmlLang(l);
  window.dispatchEvent(new Event("av-lang-change"));
}

// Cambio verso una nuova lingua: serve il dizionario prima di disegnare la pagina.
export async function switchLang(l, apply) {
  await loadDict(l);
  saveLang(l);
  apply(l);
}
